import Phaser from "phaser";
import {
	aliveTicks,
	BACKDROP_HEIGHT,
	BACKDROP_WIDTH,
	BAT_RADIUS,
	batPosition,
	createWorld,
	ghostPosition,
	HIDES,
	roundStats,
	type SimEvent,
	SPOTS,
	starsFor,
	step,
	TICK_MS,
	ticksLeft,
	type WorldState,
} from "../../sim";
import type { Overlay } from "../../ui/overlay";
import { Sfx } from "../audio";
import { pixelRatio } from "../display";
import { canVibrate, vibrate } from "../haptics";
import { TapQueue } from "../input/taps";
import { type Fit, fitContain, imageToScreen, screenToImage, tapReach } from "../layout";
import { FixedStepLoop } from "../loop/fixed-step";
import { MusicPlayer } from "../music-player";
import { BAT_GLOW, GHOST_GLOW } from "../palette";
import { BatView } from "../render/bat-view";
import { Effects } from "../render/effects";
import { DEPTH_BACKDROP, DEPTH_SKY, DEPTH_STARS, GhostView } from "../render/ghost-view";
import { LightView } from "../render/light-view";
import { createTextures, TEX } from "../render/textures";
import {
	bestFor,
	loadSettings,
	recordNightmare,
	recordScore,
	type Settings,
	saveSettings,
} from "../settings";
import { SongCursor } from "../songs";
import { SceneKey } from "./scene-keys";

type Mode = "menu" | "playing" | "over";

/** How long "Kör!" stays up after the countdown (0.75 s). */
const GO_TICKS = 50;
/** The last seconds tick audibly. */
const LAST_SECONDS = 5;

/**
 * Runs a round: steps the deterministic sim at a fixed TICK_MS from Phaser's variable frame
 * loop, turns pointer presses into taps in picture coordinates and draws the lit spots on the
 * backdrop. There is deliberately no pause.
 */
export class GameScene extends Phaser.Scene {
	private world!: WorldState;
	private loop!: FixedStepLoop;
	private taps!: TapQueue;
	private overlay!: Overlay;
	private backdrop!: Phaser.GameObjects.Image;
	private sky!: Phaser.GameObjects.Image;
	private stars!: Phaser.GameObjects.Graphics;
	private readonly starField = makeStars(90);
	private effects!: Effects;
	private readonly lightViews = new Map<number, LightView>();
	private batView: BatView | null = null;
	private ghostView: GhostView | null = null;
	private fit!: Fit;
	private mode: Mode = "menu";
	private settings!: Settings;
	private sfx!: Sfx;
	private music!: MusicPlayer;
	/** The countdown text and the whole seconds left that last made a sound. */
	private lastCountdown: string | null = null;
	private lastSecond = 0;
	private seed = 0;
	/** The chosen song's position (song mode); null plays the colours' plings. */
	private song: SongCursor | null = null;

	constructor() {
		super(SceneKey.Game);
	}

	create(): void {
		this.overlay = this.registry.get("overlay") as Overlay;
		this.settings = loadSettings();
		this.sound.mute = !this.settings.sound;
		const params = new URLSearchParams(window.location.search);
		this.seed = Number(params.get("seed") ?? Math.floor(Math.random() * 2 ** 31));
		this.world = createWorld(this.seed, this.settings.seconds, this.settings);
		this.loop = new FixedStepLoop(TICK_MS);
		this.taps = new TapQueue();
		createTextures(this);
		this.sky = this.add.image(0, 0, TEX.sky).setOrigin(0, 0).setDepth(DEPTH_SKY);
		this.stars = this.add.graphics().setDepth(DEPTH_STARS);
		// Slightly dimmed: night time, and the lights stand out more.
		this.backdrop = this.add
			.image(0, 0, "backdrop")
			.setOrigin(0, 0)
			.setTint(0xb4b0c8)
			.setDepth(DEPTH_BACKDROP);
		this.effects = new Effects(this);
		this.sfx = new Sfx(this);
		this.music = new MusicPlayer(this);
		this.music.set(this.settings.music, this.settings.musicVolume);
		this.layout();
		this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
		this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this);
			this.music.stop();
		});

		// `?debug`: the live world and spot positions for tests and the console.
		if (params.has("debug")) {
			const debug = {
				// In CSS pixels, where the page's mouse clicks: the bat one tick ahead.
				batOnScreen: () => {
					const bat = this.world.bat;
					if (!bat) return null;
					const at = batPosition(bat, this.world.tick + 1);
					const p = imageToScreen(this.fit, at.x, at.y);
					return { x: p.x / pixelRatio(), y: p.y / pixelRatio() };
				},
				// Likewise for the ghost, fully out whatever its tick.
				ghostOnScreen: () => {
					const ghost = this.world.ghost;
					const hide = ghost && HIDES[ghost.hide];
					if (!ghost || !hide) return null;
					const at = ghostPosition(ghost, this.world.tick + 1);
					const p = imageToScreen(this.fit, at.x, at.y);
					return { x: p.x / pixelRatio(), y: p.y / pixelRatio(), peek: at.peek };
				},
				spotOnScreen: (spot: number) => {
					const s = SPOTS[spot];
					if (!s) return null;
					const p = imageToScreen(this.fit, s.x, s.y);
					return { x: p.x / pixelRatio(), y: p.y / pixelRatio() };
				},
			};
			Object.defineProperty(debug, "world", { get: () => this.world });
			Object.defineProperty(debug, "audio", {
				get: () => ({
					loaded: this.cache.audio.getKeys(),
					mute: this.sound.mute,
					webAudio: this.sound instanceof Phaser.Sound.WebAudioSoundManager,
					music: this.music.current,
				}),
			});
			(window as unknown as { gameDebug: unknown }).gameDebug = debug;
		}

		if (params.has("play")) this.start();
		else this.showMenu();
	}

	override update(_time: number, delta: number): void {
		const steps = this.mode === "playing" ? this.loop.advance(delta) : 0;
		for (let i = 0; i < steps; i++) this.handle(step(this.world, { taps: this.taps.take() }));
		if (this.mode === "playing") this.updateHud();
		this.draw(this.mode === "playing" ? this.loop.alpha : 0);
	}

	private showMenu(): void {
		this.mode = "menu";
		this.overlay.showMenu(
			this.settings,
			{
				onSound: (on) => {
					this.sound.mute = !on;
					saveSettings(this.settings);
				},
				onMusic: () => {
					this.music.set(this.settings.music, this.settings.musicVolume);
					saveSettings(this.settings);
				},
				onChange: () => saveSettings(this.settings),
				onStart: () => {
					// On phones and tablets, fill the screen (the tap is the gesture browsers require).
					if (window.matchMedia("(pointer: coarse)").matches && !this.scale.isFullscreen) {
						this.scale.startFullscreen();
					}
					this.start();
				},
				onRules: () => this.overlay.showRules(() => this.showMenu()),
				onScores: () =>
					this.overlay.showScores(this.settings.nightmareScores, () => this.showMenu()),
			},
			canVibrate(),
		);
	}

	private updateHud(): void {
		const { world } = this;
		const tickSeconds = TICK_MS / 1000;
		const left = ticksLeft(world) * tickSeconds;
		if (world.nightmare) this.overlay.setElapsed(aliveTicks(world) * TICK_MS);
		else this.overlay.setTime(left, world.seconds);
		const second = Math.ceil(left);
		if (
			!world.nightmare &&
			world.phase === "playing" &&
			second <= LAST_SECONDS &&
			second !== this.lastSecond
		) {
			if (second > 0) this.sfx.play({ key: "tick" });
		}
		this.lastSecond = second;

		let countdown: string | null = null;
		if (world.phase === "countdown") {
			countdown = String(Math.ceil((world.startTick - world.tick) * tickSeconds));
			if (countdown !== this.lastCountdown) this.sfx.play({ key: "count" });
		} else if (world.tick - world.startTick < GO_TICKS) {
			countdown = "Kör!";
		}
		this.lastCountdown = countdown;
		this.overlay.setCountdown(countdown);
	}

	private start(): void {
		const { seconds, difficulty, missPenalty, streakMode, extras, nightmare } = this.settings;
		this.world = createWorld(this.seed++, seconds, {
			difficulty,
			missPenalty,
			streakMode,
			extras,
			nightmare,
		});
		// Every round plays the song from its start.
		this.song = this.settings.song ? new SongCursor(this.settings.song) : null;
		this.mode = "playing";
		this.loop.reset();
		this.taps.clear();
		this.lastCountdown = null;
		this.lastSecond = 0;
		for (const view of this.lightViews.values()) view.destroy();
		this.lightViews.clear();
		this.batView?.destroy();
		this.batView = null;
		this.ghostView?.destroy();
		this.ghostView = null;
		this.overlay.showGame();
		this.overlay.setScore(0);
		this.overlay.setStreak(this.world.streakMode === "off" ? null : 0);
		this.overlay.setLives(nightmare ? this.world.lives : null);
	}

	private handle(events: SimEvent[]): void {
		this.sfx.events(events, this.song);
		if (this.world.streakMode !== "off") this.overlay.setStreak(this.world.streak);
		vibrate(events, this.settings.vibrate && canVibrate());
		for (const ev of events) {
			if (ev.type === "lifeLost") {
				this.overlay.setLives(ev.lives);
			} else if (ev.type === "collected") {
				this.overlay.setScore(ev.score);
				const spot = SPOTS[ev.light.spot];
				if (!spot) continue;
				const p = imageToScreen(this.fit, spot.x, spot.y);
				const size = Math.max(40 * pixelRatio(), spot.r * 2 * this.fit.scale);
				this.effects.collected(p.x, p.y, ev.light.color, ev.light.golden, ev.points, size);
				this.lightViews.get(ev.light.id)?.destroy();
				this.lightViews.delete(ev.light.id);
			} else if (ev.type === "tricked") {
				this.overlay.setScore(ev.score);
				const spot = SPOTS[ev.light.spot];
				if (spot) {
					const p = imageToScreen(this.fit, spot.x, spot.y);
					const size = Math.max(40 * pixelRatio(), spot.r * 2 * this.fit.scale);
					this.effects.tricked(p.x, p.y, ev.penalty, size);
				}
				this.lightViews.get(ev.light.id)?.destroy();
				this.lightViews.delete(ev.light.id);
			} else if (ev.type === "streak") {
				this.overlay.setScore(ev.score);
				this.overlay.showStreak(ev.count, ev.bonus);
			} else if (ev.type === "batCame") {
				this.batView?.destroy();
				this.batView = new BatView(this, ev.bat);
			} else if (ev.type === "batCaught") {
				this.overlay.setScore(ev.score);
				const p = imageToScreen(this.fit, ev.x, ev.y);
				this.effects.caught(p.x, p.y, ev.points, BAT_RADIUS * 2 * this.fit.scale, BAT_GLOW);
				this.batView?.destroy();
				this.batView = null;
			} else if (ev.type === "batGone") {
				this.batView?.fadeOut();
				this.batView = null;
			} else if (ev.type === "ghostCame") {
				this.ghostView?.destroy();
				this.ghostView = new GhostView(this, ev.ghost);
			} else if (ev.type === "ghostCaught") {
				this.overlay.setScore(ev.score);
				const p = imageToScreen(this.fit, ev.x, ev.y);
				const size = (HIDES[ev.ghost.hide]?.r ?? 0) * 2 * this.fit.scale;
				this.effects.caught(p.x, p.y, ev.points, size, GHOST_GLOW);
				this.ghostView?.destroy();
				this.ghostView = null;
			} else if (ev.type === "ghostGone") {
				this.ghostView?.destroy();
				this.ghostView = null;
			} else if (ev.type === "missed") {
				this.overlay.setScore(ev.score);
				const p = imageToScreen(this.fit, ev.tap.x, ev.tap.y);
				this.effects.missed(p.x, p.y, ev.penalty);
			} else if (ev.type === "timeUp") this.gameOver(ev.score);
		}
	}

	private gameOver(score: number): void {
		this.mode = "over";
		this.overlay.setCountdown(null);
		const again = () => this.start();
		const menu = () => this.showMenu();
		if (this.world.nightmare) {
			const ms = aliveTicks(this.world) * TICK_MS;
			const run = { score, ms, date: new Date().toISOString() };
			const { bestScore, ...records } = recordNightmare(this.settings, run);
			saveSettings(this.settings);
			this.overlay.setElapsed(ms);
			this.overlay.showOver(
				{
					score,
					stars: 1,
					best: bestScore,
					newBest: records.newTime || records.newScore,
					stats: roundStats(this.world),
					nightmare: { ms, ...records },
				},
				again,
				menu,
			);
			return;
		}
		this.overlay.setTime(0, this.world.seconds);
		const { seconds, difficulty } = this.world;
		const newBest = recordScore(this.settings, score);
		if (newBest) saveSettings(this.settings);
		this.overlay.showOver(
			{
				score,
				stars: starsFor(score, seconds, difficulty),
				best: bestFor(this.settings),
				newBest,
				stats: this.world.streakMode === "off" ? null : roundStats(this.world),
			},
			again,
			menu,
		);
	}

	private onPointerDown(pointer: Phaser.Input.Pointer): void {
		if (this.mode !== "playing") return;
		this.effects.ripple(pointer.x, pointer.y, 60);
		const { x, y } = screenToImage(this.fit, pointer.x, pointer.y);
		this.taps.push({ x, y, reach: tapReach(this.fit, pixelRatio()) });
	}

	private layout(): void {
		this.fit = fitContain(this.scale.width, this.scale.height, BACKDROP_WIDTH, BACKDROP_HEIGHT);
		const { width, height } = this.scale;
		this.backdrop.setPosition(this.fit.x, this.fit.y).setScale(this.fit.scale);
		this.sky.setDisplaySize(width, height);
		this.stars.clear();
		const px = pixelRatio();
		for (const star of this.starField) {
			this.stars.fillStyle(0xffffff, star.alpha);
			this.stars.fillCircle(star.x * width, star.y * height * 0.75, star.r * px);
		}
	}

	/** Keeps one LightView per lit light; `alpha` = progress towards the next tick. */
	private draw(alpha: number): void {
		const tick = this.world.tick + alpha;
		const time = this.time.now;
		this.batView?.update(this.fit, tick, time);
		this.ghostView?.update(this.fit, tick, time);
		for (const [id, view] of this.lightViews) {
			if (!this.world.lights.some((l) => l.id === id)) {
				view.fadeOut();
				this.lightViews.delete(id);
			}
		}
		for (const light of this.world.lights) {
			let view = this.lightViews.get(light.id);
			if (!view) {
				const spot = SPOTS[light.spot];
				if (!spot) continue;
				view = new LightView(this, light, spot);
				this.lightViews.set(light.id, view);
			}
			view.update(this.fit, tick, time);
		}
	}
}

/** Stars for the sky, in screen fractions (cosmetic, so Math.random is fine here). */
function makeStars(count: number): { x: number; y: number; r: number; alpha: number }[] {
	return Array.from({ length: count }, () => ({
		x: Math.random(),
		y: Math.random(),
		r: 0.6 + Math.random() * 1.2,
		alpha: 0.3 + Math.random() * 0.6,
	}));
}
